import React, { useState, useRef, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  Grid,
  TextField,
  Divider,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  MenuItem,
  IconButton,
  Box,
  ListSubheader,
  Select,
  FormControl,
  InputLabel,
  FormHelperText
} from "@mui/material";
import SignatureCanvas from "react-signature-canvas";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { ThemeProvider } from '@mui/material/styles';
import ShareIcon from '@mui/icons-material/Share';
import theme from '../../theme/theme.js';
import DraftSaveButton from '../../components/DraftSaveButton';
import { DRAFT_KEYS, clearDraft, loadDraft, hasDraft } from '../../utils/draftUtils';
import { useNotification } from '../../contexts/NotificationContext';
import { API_BASE_URL } from '../../lib/constants';
const PostOperativeOrders = () => {
  const { showNotification } = useNotification();
  // Removed duplicate signaturePadRef declaration
  const uhid_no = useParams().uhid_no;
  const [formData, setFormData] = useState({
    uhid_no: uhid_no || '',
    lab_pathology: "No",
    lab_cytology: "No",
    lab_microbiology: "No",
    lab_biochemistry: "No",
    doctor_signature: "", // base64 encoded signature
    order_datetime: "",
    doctor_name: "",
    doctor_name_signature: ""


  });
  // Add loading and error state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Extract uhid_no directly from params
  const [success, setSuccess] = useState(false);
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [currentSignatureRole, setCurrentSignatureRole] = useState("");
  const signaturePadRef = useRef(null);

  const handleSignatureOpen = (role) => {
    setCurrentSignatureRole(role);
    setSignatureDialogOpen(true);
  };
  const handleDialogClose = () => {
    setSignatureDialogOpen(false);
  };
  const handleSignatureClear = () => {
    signaturePadRef.current.clear();
  };

  const handleSignatureSave = () => {
    const dataURL = signaturePadRef.current.toDataURL();

    // Removed undefined 'imageToBase64' function call
    setFormData((prevData) => ({
      ...prevData,
      [currentSignatureRole]: dataURL.split(',')[1],
    }));
    if (errors[currentSignatureRole]) {
      setErrors(prev => ({ ...prev, [currentSignatureRole]: '' }));
    }
    setSignatureDialogOpen(false);
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    let error = "";
    switch (name) {
      case 'order_datetime':
        if (!value) error = "DateTime is required";
        break;
      case 'doctor_name':
        if (!value) error = "Doctor Name is required";
        break;
      default:
        break;
    }
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };
  // Add this above validate function
  const [errors, setErrors] = useState({});

  const validate = () => {
    const tempErrors = {};

    // Check required fields
    if (!formData.order_datetime) {
      tempErrors.order_datetime = "DateTime is required";
    }
    if (!formData.doctor_name_signature) {
      tempErrors.doctor_name_signature = "Doctor Signature is required"
    }
    if (!formData.doctor_name) {
      tempErrors.doctor_name = "Doctor Name is required"
    }

    // Add other validation rules as needed
    // if (!formData.patient_name) {
    //   tempErrors.patient_name = "Patient name is required";
    // }

    setErrors(tempErrors);

    // If there are errors, scroll to the first error field
    if (Object.keys(tempErrors).length > 0) {
      // Get the first error field name
      const firstErrorField = Object.keys(tempErrors)[0];

      // Find the element with error and scroll to it
      setTimeout(() => {
        const errorElement = document.querySelector(`[name="${firstErrorField}"]`);
        if (errorElement) {
          errorElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
          // Focus on the field to draw attention
          errorElement.focus();
        }
      }, 100);

      // Show a general notification to inform user about validation errors
      showNotification('error', `Please fill in all required fields.`);
    }

    return Object.keys(tempErrors).length === 0;
  };
  const doctorGroups = {
    Cardiologists: [
      "Dr. John Smith",
      "Dr. Emily Johnson",
      "Dr. Michael Brown",
      "Dr. Sarah Davis",
      "Dr. William Wilson",
      "Dr. Linda Martinez"
    ],
    Neurologists: [
      "Dr. Robert Lee",
      "Dr. Patricia Taylor",
      "Dr. James Anderson",
      "Dr. Barbara Thomas",
      "Dr. David Jackson",
      "Dr. Susan White"
    ],
    Orthopedics: [
      "Dr. Richard Garcia",
      "Dr. Jennifer Harris",
      "Dr. Charles Clark",
      "Dr. Jessica Lewis",
      "Dr. Thomas Robinson",
      "Dr. Karen Walker"
    ],
    Dermatologists: [
      "Dr. Nancy Young",
      "Dr. Matthew Hall",
      "Dr. Donna Allen",
      "Dr. Steven King",
      "Dr. Michelle Wright",
      "Dr. George Scott"
    ],
    Gynecologists: [
      "Dr. Sandra Green",
      "Dr. Kevin Adams",
      "Dr. Laura Baker",
      "Dr. Mark Nelson",
      "Dr. Angela Carter",
      "Dr. Brian Mitchell"
    ],
    Pediatricians: [
      "Dr. Melissa Perez",
      "Dr. Eric Roberts",
      "Dr. Deborah Turner",
      "Dr. Jason Phillips",
      "Dr. Rebecca Campbell",
      "Dr. Andrew Parker"
    ],
    AnaesthesiaSpecialists: [
      "Dr. Anil Mehta",
      "Dr. Priya Iyer",
      "Dr. Rakesh Nair",
      "Dr. Neha Sharma",
      "Dr. Vivek Rao",
      "Dr. Aarti Desai"
    ]
  };
  const [savedData, setSavedData] = useState(null);
  const [showSaveNotification, setShowSaveNotification] = useState(false);
  const [currentPage, setCurrentPage] = useState('form');
  const [dataLoaded, setDataLoaded] = useState(false);
  // Auto-save functionality
  // Auto-save functionality - only after initial data is loaded
  useEffect(() => {
    if (!dataLoaded) return; // Don't auto-save until initial data is loaded

    const timeoutId = setTimeout(() => {
      if (hasFormData()) {
        saveFormData(formData);
        setShowSaveNotification(true);
        setTimeout(() => setShowSaveNotification(false), 2000);
      }
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, [formData, dataLoaded]);

  // Save form data to sessionStorage
  const saveFormData = (data) => {
    sessionStorage.setItem(`post_opertive_orderForm_${uhid_no}`, JSON.stringify(data));
    console.log('Form data saved:', data);
  };

  // Get saved form data
  const getSavedFormData = () => {
    const saved = sessionStorage.getItem(`post_opertive_orderForm_${uhid_no}`);
    return saved ? JSON.parse(saved) : null;
  };
  useEffect(() => {
    const saved = getSavedFormData();
    if (saved && Object.keys(saved).length > 0) {
      setSavedData(saved);
      setFormData(saved);
    }
  }, []);

  // Load draft data if available
  useEffect(() => {
    if (uhid_no && hasDraft(DRAFT_KEYS.POST_OPERATIVE_ORDERS, uhid_no)) {
      const saved = loadDraft(DRAFT_KEYS.POST_OPERATIVE_ORDERS, uhid_no);
      if (saved && saved.formData) {
        setFormData(saved.formData);
      }
    }
  }, [uhid_no]);
  // Check if form has any data
  const hasFormData = () => {
    return Object.values(formData).some(value => {
      if (typeof value === 'string') return value.trim() !== '';
      if (typeof value === 'object' && value !== null) {
        return Object.values(value).some(subValue =>
          typeof subValue === 'string' ? subValue.trim() !== '' : subValue
        );
      }
      return value === true;
    });
  };

  // Clear saved data - ONLY called on successful submit
  const clearSavedData = () => {
    setSavedData(null);
    sessionStorage.removeItem(`post_opertive_orderForm_${uhid_no}`);
    sessionStorage.removeItem(`formStatus_post_opertive_order_${uhid_no}`);
  };

  // Home button handler
  const handleHomeNavigation = () => {
    if (hasFormData()) {
      saveFormData(formData);
      // Store form status for home page to show "Continue" button
      sessionStorage.setItem(`formStatus_post_opertive_order_${uhid_no}`, JSON.stringify({
        formName: 'POST-OPERATIVE ORDERS',
        uhid_no: uhid_no,
        status: 'incomplete',
        lastSaved: new Date().toISOString(),
        continueUrl: `/intraOperativeOrders/${uhid_no}`
      }));
    }
    navigate('/home');
  };
  const handleBack = () => {
    navigate(`/mainPage/${formData.uhid_no}`, {
      state: { from: 'PostOperativePhase', status: 'success', step: 3 },
    }); // Navigate to the previous page
  };
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;
    // Manually format the order_datetime in the desired format (YYYY-MM-DD HH:MM:SS)
    const formattedOrderDate = new Date(formData.order_datetime);
    const formattedOrderDatetime = formattedOrderDate.toISOString().slice(0, 19).replace("T", " ");

    // Prepare the data payload
    const payload = {
      uhid_no: formData.uhid_no,
      lab_pathology: formData.lab_pathology,
      lab_cytology: formData.lab_cytology,
      lab_microbiology: formData.lab_microbiology,
      lab_biochemistry: formData.lab_biochemistry,
      doctor_name: formData.doctor_name,
      doctor_name_signature: formData.doctor_name_signature, // Base64 encoded signature
      order_datetime: formattedOrderDatetime, // Formatted datetime
    };
    try {
      let response;
      if (success) {
        response = await axios.put(`${API_BASE_URL}/update_post_operative_order/${uhid_no}`, payload);
        showNotification('success', response.data.message || "Post-operative Orders Form updated successfully!");
      } else {
        response = await axios.post(`${API_BASE_URL}/post_operative_order`, payload);
        showNotification('success', response.data.message || "Post-operative Orders Form submitted successfully!");
      }
      clearDraft(DRAFT_KEYS.POST_OPERATIVE_ORDERS, uhid_no);
      navigate(`/mainPage/${formData.uhid_no}`, {
        state: { from: 'PostOperativePhase', status: 'success', step: 3 },
      });
    } catch (error) {
      console.error("There was an error submitting the form!", error);
      const errorMessage = error.response?.data?.error || "Error in submitting Post-operative Orders form!";
      showNotification('error', errorMessage);
    }
  };
  const navigate = useNavigate();
  useEffect(() => {
    if (!uhid_no) return;

    const fetchData = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/get_post_operative_order/${uhid_no}`
        );

        console.log("API Response Data:", response.data);

        const backendData = response.data;

        if (backendData && Object.keys(backendData).length > 0) {
          setFormData((prevData) => ({
            ...prevData,
            ...backendData,
            uhid_no,
            order_datetime: backendData.order_datetime ?? prevData.order_datetime,
            doctor_name_signature: backendData.doctor_name_signature ?? prevData.doctor_name_signature,
          }));
          setSuccess(true);
        }
      } catch (error) {
        console.error("Error fetching post operative order:", error);
      }
    };

    fetchData();
    setDataLoaded(true);
  }, [uhid_no]);

  const fetchPatientData = async (uhidNo) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/get_patient/${uhidNo}`);

      console.log(response.data);
      setFormData(prev => ({
        ...prev,

        name: response.data.name

      }));
      setLoading(false);
    } catch (error) {
      console.error('Error fetching patient data:', error);
      setError('Failed to fetch patient data. Please try again.');
      setLoading(false);
    }
  };
  //    useEffect(() => {
  //   const fetchUhid = async () => {
  //     try {
  //       const response = await axios.get(`${API_BASE_URL}/postop_orders_next_uhid`);
  //       setFormData(prev => ({
  //         ...prev,
  //         uhid_no: response.data.next_uhid
  //       }));
  //     } catch (error) {
  //       console.error('Failed to fetch UHID:', error);
  //     }
  //   };

  //   fetchUhid();
  // }, []);

  const goToVideoRecorder = (formName, uhid_no) => {
    navigate(`/video?form=${encodeURIComponent(formName)}&uhid_no=${encodeURIComponent(uhid_no)}`);
  };
  const [submitStatus, setSubmitStatus] = useState({
    open: false,
    message: "",
    severity: "success"
  });
  const handleShareClick = async () => {
    try {
      // Call API to generate a share token for the given UHID
      const res = await axios.post(`${API_BASE_URL}/generate_for_post_op_orders_api_share_token/${uhid_no}`);

      const token = res.data.token;
      const link = `${API_BASE_URL}/postOperativeOrders/${uhid_no}?token=${token}`;
      const message = `Dear Doctor,\n\nPlease fill the post-operative analgesia chart:\n\n${link}`;

      // Copy the shareable link to clipboard
      await navigator.clipboard.writeText(link);

      // Show success notification to user
      setSubmitStatus({
        open: true,
        message: "Secure link copied to clipboard!",
        severity: "info",
      });

      // Open WhatsApp chat with pre-filled message (replace phone number if needed)
      const phoneNumber = '+916379605575'; // no spaces or special chars for wa.me
      window.open(`http://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`, '_blank');
    } catch (error) {
      console.error("Error generating share token:", error);

      // Show error notification to user
      setSubmitStatus({
        open: true,
        message: "Failed to generate shareable link",
        severity: "error",
      });
    }
  };
  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="md" sx={{ marginTop: 4, marginBottom: 4 }}>
        <Paper elevation={3} sx={{ padding: 3 }}>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 2 }}>
            {/* Heading with Icon */}
            <Typography
              variant="h4"
              sx={{
                color: '#008080',
                display: 'flex',
                alignItems: 'center',
                fontWeight: 600,
                letterSpacing: 0.5,
              }}
            >

              POST-OPERATIVE ORDERS
            </Typography>

            {/* Right-aligned Logo and Text */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <img src="/logo.png" alt="Logo" className="w-8 h-8" />
              <Typography
                variant="h6"
                sx={{
                  color: '#008080',
                  marginLeft: 1,
                  fontWeight: 500,
                  letterSpacing: 0.3,
                }}
              >
                OTMS
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
            {/* Left-aligned Back Button */}
            <Button
              variant="outlined"
              onClick={handleBack}
              sx={{ marginLeft: 2 }}
            >
              Back
            </Button>

            {/* Right-aligned Buttons */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Button
                variant="contained"
                onClick={handleHomeNavigation}
                sx={{ marginRight: 1 }}
              >
                Home
              </Button>
              <Button
                variant="contained"
                onClick={() => goToVideoRecorder("POST-OPERATIVE ORDERS", uhid_no)}
                sx={{ marginRight: 1 }}
              >
                Add Iterative Video
              </Button>
              <IconButton color="primary" aria-label="share" onClick={handleShareClick}>
                <ShareIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Patient's UHID */}
          <TextField
            label="UHID No"
            fullWidth
            value={formData.uhid_no}
            onChange={(e) => {
              const val = e.target.value;
              setFormData(prev => ({ ...prev, uhid_no: val }));
            }}
            sx={{ marginTop: 2 }}
            InputProps={{ readOnly: true, }}
          />

          {/* Lab Options */}
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6">Lab Orders</Typography>
          <Grid container spacing={2}>
            {["Pathology", "Cytology", "Microbiology", "Biochemistry"].map(
              (type) => (
                <Grid item xs={12} key={type}>
                  <TextField
                    fullWidth
                    select
                    label={`Select ${type}`}
                    value={formData[`lab_${type.toLowerCase()}`]}
                    onChange={(e) =>
                      setFormData(prev => ({
                        ...prev,
                        [`lab_${type.toLowerCase()}`]: e.target.value,
                      }))
                    }
                    variant="outlined"
                    sx={{ marginTop: 1 }}
                  >
                    <MenuItem value="Yes">Yes</MenuItem>
                    <MenuItem value="No">No</MenuItem>
                  </TextField>
                </Grid>
              )
            )}
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography variant="h6">Order Date & Time</Typography>
          <TextField
            fullWidth
            type="datetime-local" inputProps={{
              min: new Date().toISOString().slice(0, 16), // Sets the minimum date and time to the current date and time
            }}
            InputLabelProps={{ shrink: true }}
            label="Order Date & Time"
            name="order_datetime"
            required
            error={Boolean(errors.order_datetime)}
            helperText={errors.order_datetime}
            value={formData.order_datetime}
            onChange={handleChange}
            onBlur={handleBlur}
            variant="outlined"
          />

          <Divider sx={{ my: 2 }} />
          <Typography variant="h6">Signatures</Typography>

          {/* Signatures Section */}
          <Grid container spacing={2}>
            {[
              {
                label: "Doctor Name & Signature",
                key: "doctor_name_signature",
                key1: "doctor_name",
                phone: "+916379605575" // <-- Replace with actual phone or make dynamic if needed
              }
            ].map((person, index) => (
              <Grid item xs={12} key={index}>
                <FormControl fullWidth sx={{ marginTop: 1 }}>
                  <InputLabel id={`${person.key1}-label`}>Name ({person.label})</InputLabel>
                  <Select
                    labelId={`${person.key1}-label`}
                    id={person.key1}
                    name={person.key1}
                    value={formData[person.key1] || ""}
                    label={`Name (${person.label})`}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                  >
                    <MenuItem value="">
                      <em>Select Nurse</em>
                    </MenuItem>
                    {Object.entries(doctorGroups).map(([groupName, nurses]) => [
                      <ListSubheader key={groupName}>{groupName}</ListSubheader>,
                      ...nurses.map((nurse, idx) => (
                        <MenuItem key={`${groupName}-${idx}`} value={nurse}>
                          {nurse}
                        </MenuItem>
                      ))
                    ])}
                  </Select>
                  {errors[person.key1] && <FormHelperText>{errors[person.key1]}</FormHelperText>}
                </FormControl>

                <Button
                  variant="outlined"
                  color="primary"
                  sx={{ marginTop: 2, marginRight: 1 }}
                  error={Boolean(errors.consultant)}
                  helperText={errors.consultant}
                  onClick={() => handleSignatureOpen(person.key)}
                >
                  ✍️ Sign Here
                </Button>

                <Button
                  color="success"
                  variant="outlined"
                  sx={{ marginTop: 2 }}
                  onClick={() => {
                    const token = "abc123"; // Ideally generated securely
                    const link = `${API_BASE_URL}/signature-form/${formData.uhid_no}/${token}`;
                    const message = `Dear Doctor, please sign the post-operative form here: ${link}`;
                    window.open(`https://wa.me/${person.phone}?text=${encodeURIComponent(message)}`, '_blank');
                  }}

                >
                  📤 Send to Doctor (WhatsApp)
                </Button>

                {formData[person.key] && (
                  <img
                    src={`data:image/png;base64,${formData[person.key]}`}
                    alt="Signature"
                    style={{ marginTop: 10, border: '1px solid black', width: '100%', height: '150px' }}
                  />
                )}
              </Grid>
            ))}
          </Grid>


          <Dialog open={signatureDialogOpen} onClose={() => setSignatureDialogOpen(false)}>
            <DialogTitle>Provide Your Signature</DialogTitle>
            <DialogContent>
              <SignatureCanvas ref={signaturePadRef} canvasProps={{ width: 400, height: 200, className: 'sigCanvas' }} />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleDialogClose} >Close</Button>
              <Button onClick={handleSignatureClear} color="secondary">Clear</Button>
              <Button onClick={handleSignatureSave} color="primary">Done</Button>
            </DialogActions>
          </Dialog>




          {/* Save Draft and Submit Button */}
          <Divider sx={{ my: 2 }} />
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <DraftSaveButton
                formKey={DRAFT_KEYS.POST_OPERATIVE_ORDERS}
                uhidNo={uhid_no}
                formData={formData}
                onDraftLoaded={(draftData) => setFormData(draftData)}
                variant="outlined"
                size="large"
                isUpdate={success}
              />
              <Button
                type="submit"
                variant="contained"
                color="primary"
                sx={{ marginTop: 3 }}
                fullWidth
                onClick={handleSubmit}
              >
                {success ? 'Update' : 'Submit'}
              </Button>
            </Box>
          </Grid>

        </Paper>
      </Container>
    </ThemeProvider>
  );
};

export default PostOperativeOrders;
